import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { groupBy, orderBy } from "lodash";
import CircularProgressbar from "react-circular-progressbar";

import InformationalTooltip from "../InformationalTooltip";

const DepartmentTimeUsage = ({ calls, totals, dark }) => {
  const tasks = useMemo(
    () =>
      calls
        .map((call) =>
          call?.tasks?.map((task) => ({
            ...call.service.tasks.find((st) => st._id === task.service_task),
            id: task.service_task,
            duration: task.actual_duration,
            durationUnits: task.duration_units
          }))
        )
        .filter((arr) => arr.length)
        .flat(1),
    [calls]
  );

  const groupedTasks = useMemo(
    () => groupBy(tasks, (task) => task.task?.description),
    [tasks]
  );

  const totalDuration = useMemo(() => {
    let duration = 0;
    Object.keys(groupedTasks).forEach((task) => {
      let minutes = 0;
      groupedTasks[task].forEach((t) => {
        if (t.durationUnits === "Hours") {
          minutes += t.duration * 60;
        } else {
          minutes += t.duration;
        }
      });
      const totalHours = Math.floor(minutes / 60);
      const totalMinutes = minutes % 60;
      const totalSeconds = totalHours * 3600 + totalMinutes * 60;
      groupedTasks[task].totalHours = totalHours;
      groupedTasks[task].totalMinutes = totalMinutes;
      groupedTasks[task].totalSeconds = totalSeconds;
      duration += totalSeconds;
    });
    return duration;
  }, [groupedTasks]);

  const billablePercentage = useMemo(() => {
    if (totals.actualHours > 0) {
      if (totals.billableHours <= totals.actualHours) {
        return (totals.billableHours / totals.actualHours) * 100;
      }
      return 100;
    }
    return 0;
  }, [totals]);

  const finalTasks = useMemo(() => {
    const formattedTasks = Object.keys(groupedTasks).map((task) => ({
      description: groupedTasks[task][0].task?.description || "N/A",
      percentage: (groupedTasks[task].totalSeconds / totalDuration) * 100
    }));
    const ordered = orderBy(formattedTasks, "percentage", "desc");
    return ordered.slice(0, 10);
  }, [groupedTasks, totalDuration]);

  return (
    <div className="content-wrapper time-usage-breakdown-container">
      <div className="header-section-container">
        <h1>Department Time Usage</h1>
        <InformationalTooltip
          id="time_usage"
          dark={dark}
          content="Tasks with the most time applied and the overall percentage of hours that are billable"
        />
      </div>
      {tasks.length > 0 && (
        <div className="task-bar-wrapper">
          <div className="task-bar-list">
            <div className="top-tasks-title-container">
              <h4 className="top-tasks-title">Top Tasks</h4>
              <div>&nbsp;</div>
            </div>
            {finalTasks.map((task) => (
              <div key={task} className="task-bar-container">
                <h4>{task?.description || "N/A"}</h4>
                <div className="task-bar">
                  <span className="percentage">
                    {task.percentage.toFixed(1).replace(/[.,]0$/, "")}%
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="task-bar-progressbar">
            <CircularProgressbar
              percentage={billablePercentage}
              strokeWidth={10}
              text={`${billablePercentage
                .toFixed(1)
                .replace(/[.,]0$/, "")}% Billable`}
            />
          </div>
        </div>
      )}
      {tasks.length === 0 && (
        <div className="no-tasks-container">
          <h4>No Data To Show</h4>
        </div>
      )}
    </div>
  );
};

DepartmentTimeUsage.propTypes = {
  calls: PropTypes.array,
  totals: PropTypes.object,
  dark: PropTypes.bool
};

DepartmentTimeUsage.defaultProps = {
  calls: [],
  totals: {
    actualHours: 0,
    billableHours: 0
  },
  dark: false
};

export default DepartmentTimeUsage;
